/* ============================================================
   screen-landing.jsx — Landing / Home
   ============================================================ */

function Landing({ go, startPrompt, theme }) {
  const [val, setVal] = React.useState("");
  const examples = [
    "비 내리는 네온 도시의 밤거리",
    "수채화 질감의 봄 꽃다발",
    "유리로 만든 초현실 액체 조각",
    "복고풍 신스웨이브 일몰",
  ];
  const featured = GALLERY.slice(0, 10);
  const collage = [
    { seed: 100, palette: "dusk", r: "3 / 4" },
    { seed: 113, palette: "sunset", r: "1 / 1" },
    { seed: 126, palette: "ocean", r: "1 / 1" },
    { seed: 139, palette: "bloom", r: "3 / 4" },
  ];

  const submit = () => startPrompt(val || examples[0]);

  return (
    <div>
      {/* ---------- HERO ---------- */}
      <section style={{ position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          background: "radial-gradient(900px 480px at 78% -8%, rgba(91,73,255,.12), transparent 60%), radial-gradient(700px 420px at 10% 0%, rgba(255,90,168,.09), transparent 55%)",
        }} />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "clamp(48px, 7vw, 92px) 24px clamp(40px, 5vw, 64px)" }}>
          <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1.05fr .95fr", gap: 48, alignItems: "center" }}>
            {/* left: copy + prompt bar */}
            <div>
              <span className="badge badge-spark" style={{ height: 28, padding: "0 12px", fontSize: 13 }}>
                <Icon name="sparkles" size={14} stroke={2} /> AI 이미지 스튜디오
              </span>
              <h1 style={{ fontSize: "clamp(38px, 5.4vw, 64px)", marginTop: 18, lineHeight: 1.08 }}>
                상상을 입력하면,<br />
                <span style={{
                  background: "var(--spark)", WebkitBackgroundClip: "text", backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>작품이 됩니다.</span>
              </h1>
              <p style={{ fontSize: "clamp(16px, 1.6vw, 19px)", color: "var(--ink-2)", marginTop: 18, maxWidth: 480 }}>
                한 줄의 프롬프트로 고해상도 이미지를 만들고, 크리에이터들의 갤러리에서
                영감을 얻으세요. 디자이너를 위한 AI 이미지 워크플로우.
              </p>

              {/* prompt bar */}
              <div style={{ marginTop: 28 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, padding: 8,
                  background: "var(--paper)", border: "1px solid var(--line-2)",
                  borderRadius: "var(--r-pill)", boxShadow: "var(--sh-2)",
                }}>
                  <Icon name="wand" size={20} stroke={2} style={{ color: "var(--accent)", marginLeft: 10 }} />
                  <input
                    value={val} onChange={(e) => setVal(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder="만들고 싶은 이미지를 설명하세요…"
                    style={{ flex: 1, border: "none", outline: "none", background: "transparent",
                      fontSize: 16, fontFamily: "inherit", color: "var(--ink)", minWidth: 0 }}
                  />
                  <button className="btn btn-spark" onClick={submit} style={{ paddingLeft: 18, paddingRight: 18 }}>
                    생성 <Icon name="arrowRight" size={17} stroke={2} />
                  </button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                  {examples.map((ex) => (
                    <button key={ex} className="chip" onClick={() => setVal(ex)} style={{ height: 30, fontSize: 13 }}>
                      {ex}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 26, marginTop: 32 }}>
                {[["240만+", "생성된 이미지"], ["18만+", "크리에이터"], ["4종", "AI 모델"]].map(([n, l]) => (
                  <div key={l}>
                    <div className="tnum" style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>{n}</div>
                    <div className="muted" style={{ fontSize: 13 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* right: floating collage */}
            <div className="hero-collage" style={{ position: "relative", height: 460 }}>
              <FloatCard {...collage[0]} style={{ width: "46%", left: "2%", top: "4%", rotate: "-4deg", z: 2 }} delay={0} />
              <FloatCard {...collage[1]} style={{ width: "40%", right: "4%", top: "0%", rotate: "5deg", z: 1 }} delay={.1} />
              <FloatCard {...collage[2]} style={{ width: "42%", left: "8%", bottom: "2%", rotate: "3deg", z: 3 }} delay={.2} />
              <FloatCard {...collage[3]} style={{ width: "44%", right: "2%", bottom: "6%", rotate: "-5deg", z: 2 }} delay={.15} />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TRENDING ---------- */}
      <section className="container" style={{ padding: "clamp(40px, 5vw, 72px) 24px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 22 }}>
          <div>
            <span className="eyebrow"><Icon name="fire" size={14} stroke={2} style={{ verticalAlign: "-2px", marginRight: 4 }} />지금 인기있는</span>
            <h2 style={{ fontSize: "clamp(26px, 3vw, 36px)", marginTop: 8 }}>크리에이터들의 작품</h2>
          </div>
          <button className="btn btn-line btn-sm" onClick={() => go("gallery")}>
            갤러리 전체보기 <Icon name="arrowRight" size={16} />
          </button>
        </div>
        <div className="masonry-cols">
          {featured.map((it, i) => (
            <div key={it.id} style={{ marginBottom: 16 }}>
              <ImageCard item={it} index={i} layout="masonry" liked={false} onLike={() => {}} onOpen={() => go("gallery")} />
            </div>
          ))}
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section style={{ background: "var(--paper)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
        <div className="container" style={{ padding: "clamp(48px, 6vw, 80px) 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <span className="eyebrow">세 단계면 충분합니다</span>
            <h2 style={{ fontSize: "clamp(28px, 3.4vw, 40px)", marginTop: 10 }}>프롬프트에서 완성작까지</h2>
          </div>
          <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {[
              { n: "01", ic: "edit", t: "설명하기", d: "만들고 싶은 장면을 한국어로 자유롭게 적어보세요. 스타일·비율·모델을 골라 디테일을 더할 수 있습니다." },
              { n: "02", ic: "zap", t: "생성하기", d: "AI가 수 초 안에 여러 시안을 만들어냅니다. 마음에 드는 결과가 나올 때까지 변형을 반복하세요." },
              { n: "03", ic: "download", t: "활용하기", d: "고해상도로 내려받아 작업에 바로 쓰거나, 갤러리에 공유해 다른 크리에이터와 영감을 나누세요." },
            ].map((s) => (
              <div key={s.n} className="card" style={{ padding: 26, borderRadius: "var(--r-lg)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{
                    width: 48, height: 48, borderRadius: 14, background: "var(--accent-soft)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--accent-press)",
                  }}><Icon name={s.ic} size={24} stroke={2} /></span>
                  <span className="tnum" style={{ fontSize: 40, fontWeight: 800, color: "var(--surface-3)" }}>{s.n}</span>
                </div>
                <h3 style={{ fontSize: 21, marginTop: 18 }}>{s.t}</h3>
                <p style={{ color: "var(--ink-2)", marginTop: 8, fontSize: 15 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="container" style={{ padding: "clamp(48px, 6vw, 84px) 24px" }}>
        <div style={{
          position: "relative", overflow: "hidden", borderRadius: "var(--r-xl)",
          background: "var(--ink)", color: "#fff", padding: "clamp(36px, 5vw, 64px)", textAlign: "center",
        }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(600px 300px at 50% -20%, rgba(91,73,255,.5), transparent 60%), radial-gradient(500px 300px at 90% 120%, rgba(255,90,168,.35), transparent 60%)" }} />
          <div style={{ position: "relative" }}>
            <h2 style={{ fontSize: "clamp(28px, 3.6vw, 44px)", maxWidth: 640, margin: "0 auto" }}>
              지금 첫 작품을 만들어보세요
            </h2>
            <p style={{ color: "rgba(255,255,255,.72)", marginTop: 14, fontSize: 17, maxWidth: 480, margin: "14px auto 0" }}>
              가입은 무료입니다. 매일 무료 크레딧으로 이미지를 생성할 수 있어요.
            </p>
            <button className="btn btn-spark btn-lg" onClick={() => go("generate")} style={{ marginTop: 30 }}>
              <Icon name="wand" size={19} stroke={2} /> 무료로 시작하기
            </button>
          </div>
        </div>
      </section>

      <Footer go={go} theme={theme} />
    </div>
  );
}

function FloatCard({ seed, palette, r, style, delay }) {
  return (
    <div style={{
      position: "absolute", left: style.left, right: style.right, top: style.top, bottom: style.bottom,
      width: style.width, "--rot": `rotate(${style.rotate})`, transform: "var(--rot)", zIndex: style.z,
      borderRadius: "var(--r-lg)", overflow: "hidden", boxShadow: "var(--sh-3)",
      border: "4px solid #fff", animation: `floaty 6s ease-in-out ${delay}s infinite`,
    }}>
      <ArtImage seed={seed} palette={palette} ratio={r} radius="0" />
    </div>
  );
}

function Footer({ go, style, theme }) {
  const { t } = useT();
  const isDark = theme === "dark";
  return (
    <footer style={{ background: "var(--surface-2)", color: "var(--ink-3)", borderTop: "1px solid var(--line)", ...style }}>
      <div className="container" style={{ padding: "28px 32px", display: "flex", alignItems: "center",
        justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
          {isDark ? (
            <>
              <img src="/brand/app_logo_dark.png" alt="" style={{ width: 28, height: 28, borderRadius: 8, display: "block" }} />
              <img src="/brand/text_logo_dark.png" alt="Promzi" style={{ height: 18, width: "auto", display: "block" }} />
            </>
          ) : (
            <>
              <img src="/brand/app_logo_light.png" alt="" style={{ width: 28, height: 28, borderRadius: 8, display: "block" }} />
              <img src="/brand/text_logo_light.png" alt="Promzi" style={{ height: 18, width: "auto", display: "block" }} />
            </>
          )}
          <span style={{ fontSize: 12.5, marginLeft: 6 }}>© 2026 Promzi</span>
        </div>
        <span style={{ display: "flex", gap: 20, fontSize: 13 }}>
          <a style={{ cursor: "pointer", color: "inherit" }} className="foot-link">{t("foot_terms")}</a>
          <a style={{ cursor: "pointer", color: "inherit" }} className="foot-link">{t("foot_privacy")}</a>
        </span>
      </div>
    </footer>
  );
}

Object.assign(window, { Landing, Footer });
